import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DecimalPipe } from '@angular/common';

import { ConfigureService } from '@app/configure/services/configure.service';
import { HelperService } from '@app/core/services/helper.service';

import { FinanceQuote } from '@app/core/models/finance-quote.model';

import { UISeparatedPipe } from '@app/ui/pipes/separated/separated.pipe';
import { UIPluralisePipe } from '@app/ui/pipes/pluralise/pluralise.pipe';
import { UIMoneyPipe } from '@app/ui/pipes/money/money.pipe';

@Component({
	selector: 'cng-finance-quote',
	templateUrl: './finance-quote.component.html',
	styleUrls: ['./finance-quote.component.less'],
})
export class ConfigureFinanceQuoteComponent {
	@Input() quote: FinanceQuote;
	@Input() breakdown_alignment: 'vertical' | 'horizontal' = 'vertical';
	@Input() show_info: boolean = true;
	@Input() show_status: boolean = true;
	@Input() show_disclaimer: boolean = false;
	@Input() show_vat_disclaimer: boolean = false;
	@Input() locked: boolean = false;
	vehicle: any = this.configure_service.configure_data.steps.purchase_vehicle.data;
	grand_total_vat: number = this.configure_service.configure_data.steps.customise_review.data.grand_total_vat;

	@Output() info_click: EventEmitter<any> = new EventEmitter();
	@Output() manage_application_click: EventEmitter<any> = new EventEmitter();

	constructor(
		private configure_service: ConfigureService,
		private money_pipe: UIMoneyPipe,
		private separated_pipe: UISeparatedPipe,
		private number_pipe: DecimalPipe,
		private pluralise_pipe: UIPluralisePipe,
		private helper_service: HelperService
	) {}

	showStatus(): boolean {
		return this.show_status && (this.isApproved() || this.isProcessed() || this.isSubmitted());
	}

	isApproved(): boolean {
		return this.quote.quote_status === 'Approved' || this.quote.quote_status === 'ConditionallyApproved';
	}

	isProcessed(): boolean {
		return this.quote.quote_status === 'Processed';
	}

	isSubmitted(): boolean {
		return this.quote.quote_status === 'Submitted';
	}

	getDisplayStatus(): string {
		if (this.quote.quote_status === 'Approved') {
			return 'Approved';
		}

		if (this.quote.quote_status === 'ConditionallyApproved') {
			return 'Conditionally Approved';
		}

		if (this.quote.quote_status === 'Submitted') {
			return 'Submitted';
		}

		if (this.quote.quote_status === 'Processed') {
			return 'Processed';
		}

		return 'Quote';
	}

	formatQuoteValue(value: number, pipe: string, pipeOptions: string[] = null, suffix: string = null): string {
		if (value === null) {
			return 'TBC';
		}

		if (pipe) {
			switch (pipe) {
				case 'money':
					return this.money_pipe.transform(value);
				case 'separated':
					return this.separated_pipe.transform(value);
				case 'number':
					if (suffix) {
						return this.number_pipe.transform(value, ...pipeOptions) + suffix;
					}

					return this.number_pipe.transform(value, ...pipeOptions);
				case 'pluralise':
					return this.pluralise_pipe.transform(value, pipeOptions[0], pipeOptions[1]);
			}
		}

		return value + suffix;
	}

	getActualStatus(): { name: string; friendly_name: string; icon: string } {
		const status = this.helper_service.getFinanceStatus(this.quote.status);

		return {
			name: status,
			friendly_name: this.helper_service.convertCase(status, 'pascal', 'title'),
			icon: this.helper_service.getFinanceStatusIcon(status),
		};
	}

	showInfo(event): void {
		this.info_click.emit(event);
	}

	onManageFinanceApplication(): void {
		this.manage_application_click.emit();
	}
}
